import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OpsInputComponent } from '../ops-input/ops-input.component';
import { UsersService } from '@ops/shared-types';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ops-register',
  standalone: true,
  imports: [CommonModule, OpsInputComponent, TranslateModule],
  templateUrl: './ops-register.component.html',
  styleUrl: './ops-register.component.scss',
})
export class OpsRegisterComponent implements OnInit, OnDestroy {
  badLogin: boolean;
  username: string;
  password: string;
  fullName: string;
  email: string;
  confirmPassword: string;
  errorsTrack = {};
  jwtUserValues: any = {};
  subscriptions: Array<Subscription> = [];
  isLoading: boolean = false;
  isInvalidToken: boolean = false;

  constructor(
    private userService: UsersService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    const token = this.route.snapshot.params['token'];
    
    if (!token) {
      this.handleInvalidToken();
      this.isLoading = false;
      return;
    }

    const sub = this.userService.isValidToken(token).subscribe({
      next: (resp: any) => {
        if (!resp) {
          this.handleInvalidToken();
          this.isLoading = false;
          return;
        }

        this.parseJwtToken(token);
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Token validation error:', error);
        this.handleInvalidToken();
        this.isLoading = false;
      }
    });

    this.subscriptions.push(sub);
  }

  private parseJwtToken(token: string): void {
    try {
      const stringToken = token.split('.');
      if (stringToken.length < 3) {
        throw new Error('Invalid JWT token format');
      }

      const tokenUserPart = stringToken[1];
      const decodedPart = atob(tokenUserPart);
      const parsedValues = JSON.parse(decodedPart) as any;
      this.jwtUserValues = parsedValues?.sub || {};

      this.buildFullName();
      this.setEmail();
      this.isLoading = false;
    } catch (error) {
      console.error('JWT parsing error:', error);
      this.handleInvalidToken();
      this.isLoading = false;
    }
  }

  private buildFullName(): void {
    const fullName = [];
    if (this.jwtUserValues?.firstName) {
      fullName.push(this.jwtUserValues.firstName);
    }
    if (this.jwtUserValues?.lastName) {
      fullName.push(this.jwtUserValues.lastName);
    }
    this.fullName = fullName.join(' ');
  }

  private setEmail(): void {
    if (this.jwtUserValues?.email) {
      this.email = this.jwtUserValues.email;
    }
  }

  private handleInvalidToken(): void {
    this.isInvalidToken = true;
  }

  openRegisterPage(): void {
    window.open('https://ops.inc/register', '_self');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  @HostListener('document:keydown', ['$event']) onKeyDown(e: any) {
    if (e.key === 'Enter') {
      setTimeout(() => {
        this.verifyUser();
      }, 100);
      return;
    }
  }

  onInputUsername(event: any) {
    this.username = event.target.value;
  }

  onErrorHandler(error: string, field?: string) {
    if (!error || error === '') {
      delete this.errorsTrack[field];
    } else {
      this.errorsTrack[field] = error;
    }
  }

  get isDisabled(): boolean {
    if (
      this.password &&
      this.confirmPassword &&
      this.password === this.confirmPassword &&
      Object.keys(this.errorsTrack).length === 0
    ) {
      return false;
    }

    return true;
  }

  get passwordMissmatch(): boolean {
    if (
      this.password &&
      this.confirmPassword &&
      Object.keys(this.errorsTrack).length === 0
    ) {
      if (this.password !== this.confirmPassword) {
        return true;
      }
    }
    return false;
  }

  onPasswordChange(newValue: string): void {
    this.password = newValue;
  }

  onPasswordChangeRepeat(newValue: string): void {
    this.confirmPassword = newValue;
  }

  verifyUser() {
    this.badLogin = false;
    const verificationPathUrl = document.location.pathname.slice(1);
    const sub = this.userService
      .verifyUserAndUpdatePassword(this.password, verificationPathUrl)

      .subscribe({
        next: (resp) => {
          // console.log('RESP', resp);
          this.router.navigate(['/login']);
        },
        error: (e) => {
          if (e?.error) {
            const error = JSON.parse(e.error);
            window.alert(this.translateService.instant('windowAlert.generic.error', { error: error['message'] }));
          } else {
            window.alert(this.translateService.instant('windowAlert.opsRegister.somethingWentWrong'));
          }
          this.badLogin = true;
        },
      });
    this.subscriptions.push(sub);
  }
}
